import { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Link } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { Box, TextField } from '@mui/material';
import CommonListTable, { CommonListColumn } from '../Common/CommonListTable';
import { CustomerService } from '../../services/CustomerService';
import Op from '../../interfaces/SequalizeFilters/SequalizeTypes';
import { RazorpayOrderReferenceModelEnums } from '../../enums/RazorpayOrderReferenceModelEnums';
import moment from 'moment';

interface InputProps {
    customerId: number;
}

enum RazorpayOrderStateEnum {
    INITIATED = 'INITIATED',
    CREATED = 'CREATED',
    ATTEMPTED = 'ATTEMPTED',
    PAID = 'PAID',
    FAILED = 'FAILED',
}

const customerService = new CustomerService();

const toDateTimeLocal = (d: Date): string => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const defaultCreatedFrom = (): string => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return toDateTimeLocal(d);
};

const defaultCreatedTo = (): string => toDateTimeLocal(new Date());

function getReferenceLink(row: any): string {
    const referenceId = row?.referenceId;
    const modelName = row?.referenceModelName;
    if (!referenceId || !modelName) return '#';
    switch (modelName) {
        case RazorpayOrderReferenceModelEnums.ONE_MEAL_SERVICE:
            return `/#/OneMealService/${referenceId}/show`;
        case RazorpayOrderReferenceModelEnums.TRIAL_V3:
            return `/#/Trials/${referenceId}/show`;
        case RazorpayOrderReferenceModelEnums.USER_BUNDLE_PAYMENTS:
            return `/#/UserBundles/${referenceId}/show`;
        case RazorpayOrderReferenceModelEnums.DISCOUNT_COUPON_ORDER:
            return `/#/Combo/${referenceId}/show`;
        case RazorpayOrderReferenceModelEnums.CHEF_FOR_PARTY:
            return `/#/ChefForParty/${referenceId}/show`;
        case RazorpayOrderReferenceModelEnums.PROMOTIONAL_BALANCE:
            return `/#/PromotionalBalance/${referenceId}/show`;
        case RazorpayOrderReferenceModelEnums.CUSTOMER_TO_VENDOR_TIP:
            return `/#/VendorTip/${referenceId}/show`;
        default:
            return '#';
    }
}

const COLUMNS: CommonListColumn[] = [
    { label: 'Id', field: 'id' },
    { label: 'Razorpay Order Id', render: (row) => row?.razorpayOrderId || '---' },
    { label: 'Payment Id', render: (row) => row?.razorpaySuccessPaymentId || '---' },
    {
        label: 'Reference',
        render: (row) => {
            const link = getReferenceLink(row);
            const refLabel = row?.referenceModelName
                ? `${row.referenceModelName}${row?.referenceId ? ` #${row.referenceId}` : ''}`
                : '---';
            return link !== '#' ? <Link href={link}>{refLabel}</Link> : <>{refLabel}</>;
        },
    },
    { label: 'Currency', render: (row) => row?.currency || '---' },
    { label: 'Amount', render: (row) => (row?.amount ? Number(row.amount).toFixed(2) : '---') },
    { label: 'State', field: 'state', type: 'status' },
    {
        label: 'Refund Amount',
        render: (row) => (row?.refundAmount ? Number(row.refundAmount).toFixed(2) : '---'),
    },
    {
        label: 'Method',
        render: (row) => row?.methodData?.method || row?.methodData?.methodName || '---',
    },
    {
        label: 'Payment Date',
        render: (row) =>
            row?.paymentDate ? moment(row.paymentDate).format('DD-MM-YYYY hh:mm A') : '---',
    },
    {
        label: 'Created At',
        render: (row) =>
            row?.createdAt ? moment(row.createdAt).format('DD-MM-YYYY hh:mm A') : '---',
    },
];

const CustomerTransaction = (props: InputProps) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState(-1);
    const [loading, setLoading] = useState(false);

    const [stateFilter, setStateFilter] = useState<string>(RazorpayOrderStateEnum.PAID);
    const [referenceModelFilter, setReferenceModelFilter] = useState<string>('');
    const [createdFrom, setCreatedFrom] = useState<string>(defaultCreatedFrom());
    const [createdTo, setCreatedTo] = useState<string>(defaultCreatedTo());
    const [paymentDateFrom, setPaymentDateFrom] = useState<string>('');
    const [paymentDateTo, setPaymentDateTo] = useState<string>('');

    const filters = useMemo(() => {
        const f: any = {};
        if (stateFilter) f.state = { [Op.eq.toString()]: stateFilter };
        if (referenceModelFilter) f.referenceModelName = { [Op.eq.toString()]: referenceModelFilter };
        if (createdFrom || createdTo) {
            f.createdAt = {};
            if (createdFrom) f.createdAt[Op.gte.toString()] = new Date(createdFrom).toISOString();
            if (createdTo) f.createdAt[Op.lte.toString()] = new Date(createdTo).toISOString();
        }
        if (paymentDateFrom || paymentDateTo) {
            f.paymentDate = {};
            if (paymentDateFrom)
                f.paymentDate[Op.gte.toString()] = new Date(paymentDateFrom).toISOString();
            if (paymentDateTo)
                f.paymentDate[Op.lte.toString()] = new Date(paymentDateTo).toISOString();
        }
        return f;
    }, [stateFilter, referenceModelFilter, createdFrom, createdTo, paymentDateFrom, paymentDateTo]);

    const refreshData = useCallback(() => {
        setLoading(true);
        customerService
            .getAllTransactionForCustomer(
                props.customerId,
                rowsPerPage,
                page * rowsPerPage,
                filters,
                { field: 'createdAt', order: 'DESC' },
            )
            .then((res: any) => {
                setData(res?.data?.data ?? []);
                setTotal(res?.data?.total ?? res?.data?.count ?? -1);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [props.customerId, rowsPerPage, page, filters]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    const resetFilters = () => {
        setStateFilter(RazorpayOrderStateEnum.PAID);
        setReferenceModelFilter('');
        setCreatedFrom(defaultCreatedFrom());
        setCreatedTo(defaultCreatedTo());
        setPaymentDateFrom('');
        setPaymentDateTo('');
        setPage(0);
    };

    return (
        <div>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>State</InputLabel>
                    <Select
                        value={stateFilter}
                        label="State"
                        onChange={(e: SelectChangeEvent) => {
                            setStateFilter(e.target.value);
                            setPage(0);
                        }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {Object.values(RazorpayOrderStateEnum).map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Reference Model</InputLabel>
                    <Select
                        value={referenceModelFilter}
                        label="Reference Model"
                        onChange={(e: SelectChangeEvent) => {
                            setReferenceModelFilter(e.target.value);
                            setPage(0);
                        }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {Object.values(RazorpayOrderReferenceModelEnums)
                            .filter(
                                (m) =>
                                    ![
                                        RazorpayOrderReferenceModelEnums.TRIAL_V3,
                                        RazorpayOrderReferenceModelEnums.DUPLICATEPAYMENT,
                                        RazorpayOrderReferenceModelEnums.USER_BUNDLE_PAYMENTS,
                                    ].includes(m),
                            )
                            .map((m) => (
                                <MenuItem key={m} value={m}>{m}</MenuItem>
                            ))}
                    </Select>
                </FormControl>

                <TextField
                    size="small"
                    label="Created From"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={createdFrom}
                    onChange={(e) => {
                        setCreatedFrom(e.target.value);
                        setPage(0);
                    }}
                />
                <TextField
                    size="small"
                    label="Created To"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={createdTo}
                    onChange={(e) => {
                        setCreatedTo(e.target.value);
                        setPage(0);
                    }}
                />
                <TextField
                    size="small"
                    label="Payment From"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={paymentDateFrom}
                    onChange={(e) => {
                        setPaymentDateFrom(e.target.value);
                        setPage(0);
                    }}
                />
                <TextField
                    size="small"
                    label="Payment To"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={paymentDateTo}
                    onChange={(e) => {
                        setPaymentDateTo(e.target.value);
                        setPage(0);
                    }}
                />

                <Button size="small" variant="outlined" onClick={resetFilters}>Reset</Button>
            </Box>

            <CommonListTable
                columns={COLUMNS}
                data={data}
                loading={loading}
                page={page}
                rowsPerPage={rowsPerPage}
                total={total}
                onPageChange={setPage}
                onRowsPerPageChange={(rows) => {
                    setRowsPerPage(rows);
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                emptyText="No Transactions Found"
            />
        </div>
    );
};

export default CustomerTransaction;
